import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, Download, Printer } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface TestReportItem {
  id: string;
  test_name: string;
  test_code: string;
  category: string;
  total_tests: number;
  total_revenue: number;
  department: string;
}

export default function TestReport() {
  const { formatCurrency } = useCurrency();
  const [reportData, setReportData] = useState<TestReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState<string>('all');

  useEffect(() => {
    loadTestReport();
  }, [startDate, endDate, departmentFilter]);

  const loadTestReport = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setReportData(getMockTestReport());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('invoice_items')
        .select(`
          id,
          item_name,
          item_code,
          quantity,
          price,
          item_type,
          invoice:invoices(issue_date)
        `)
        .in('item_type', ['lab', 'radiology']);

      if (departmentFilter !== 'all') {
        query = query.eq('item_type', departmentFilter);
      }
      if (startDate) {
        query = query.gte('invoice.issue_date', startDate);
      }
      if (endDate) {
        query = query.lte('invoice.issue_date', endDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      const grouped = (data || []).reduce((acc: Record<string, any>, item) => {
        const testKey = `${item.item_code}-${item.item_name}`;
        if (!acc[testKey]) {
          acc[testKey] = {
            id: item.id,
            test_name: item.item_name,
            test_code: item.item_code || 'N/A',
            category: item.item_type === 'lab' ? 'Laboratory' : 'Radiology',
            total_tests: 0,
            total_revenue: 0,
            department: item.item_type,
          };
        }
        
        acc[testKey].total_tests += item.quantity;
        acc[testKey].total_revenue += item.price * item.quantity;
        
        return acc;
      }, {});

      setReportData(Object.values(grouped));
    } catch (error) {
      console.error('Error loading test report:', error);
      setReportData(getMockTestReport());
    } finally {
      setLoading(false);
    }
  };

  const getMockTestReport = (): TestReportItem[] => {
    return [
      {
        id: '1',
        test_name: 'Complete Blood Count (CBC)',
        test_code: 'LAB-001',
        category: 'Laboratory',
        total_tests: 125,
        total_revenue: 62500,
        department: 'lab',
      },
      {
        id: '2',
        test_name: 'Blood Sugar (Fasting)',
        test_code: 'LAB-002',
        category: 'Laboratory',
        total_tests: 98,
        total_revenue: 29400,
        department: 'lab',
      },
      {
        id: '3',
        test_name: 'Chest X-Ray',
        test_code: 'RAD-001',
        category: 'Radiology',
        total_tests: 75,
        total_revenue: 112500,
        department: 'radiology',
      },
      {
        id: '4',
        test_name: 'ECG',
        test_code: 'RAD-002',
        category: 'Radiology',
        total_tests: 60,
        total_revenue: 60000,
        department: 'radiology',
      },
    ];
  };

  const filteredData = reportData.filter(item => {
    const matchesSearch = 
      item.test_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.test_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalTests = filteredData.reduce((sum, item) => sum + item.total_tests, 0);
  const totalRevenue = filteredData.reduce((sum, item) => sum + item.total_revenue, 0);

  const handleExportCSV = () => {
    const headers = ['Test Name', 'Test Code', 'Category', 'Total Tests', 'Total Revenue'];
    const rows = filteredData.map(item => [
      item.test_name,
      item.test_code,
      item.category,
      item.total_tests,
      item.total_revenue,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `test-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Test Report</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Test Types</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{filteredData.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Tests Performed</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">{totalTests}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Revenue</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalRevenue)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search test name, code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Departments</option>
                <option value="lab">Laboratory</option>
                <option value="radiology">Radiology</option>
              </select>
            </div>
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Tests
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Revenue
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                      No test data found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.test_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.test_code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          item.category === 'Laboratory' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {item.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.total_tests}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-bold">
                        {formatCurrency(item.total_revenue)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
